import React, {Fragment, useEffect, useLayoutEffect, useState} from "react";
import styled from "styled-components";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useNavigationType} from "react-router";
import {
	fetchDetailPageScroll,
	fetchVoucherInfo,
	updateVoucherInfoList
} from "redux/slices/sales/salesSlice";

import {getPaymentTypeName, getServiceTypeName} from "utils/define";
import {getCommaNumber} from "utils/format";

import Filter from "components/ui/Filter";
import Observer from "components/ui/Observer";
import BottomBranch from "components/ui/popup/BottomBranch";



// 요약 막대(components/salesPayment/colors의 SERVICE_COLOR)와 같은 색. 이 브랜치에는 그 모듈이 아직 없어 값을 맞춰 둔다.
const SERVICE_COLOR = {
	PLACE : "#2F8F46",
	APPOINTMENT : "#E8792E",
	CLASS : "#7A4FD1",
	OPTION : "#D1367A",
	ETC : "#8A91A0",
	TRANSFER_COMMISSION : "#C9A227"
};

// CS25-8980: 취소·대기 표시는 주문 상태가 아니라 결제의 표시 상태로 정한다.
const getStatusClass = (status) => (
	(status == "CANCELLED" || status == "PAYMENT_CANCELLED") ? "cancel" :
	(status == "WAIT" || status == "PAYMENT_WAIT") ? "wait" : ""
);
// 취소선은 계산되지 않는 결제(낸 금액 0)에만, 음수 금액은 빨간색으로 보인다.
const getMoneyClass = (status, amount, paidAmount) => (
	(getStatusClass(status) == "wait" ? " wait" : "") +
	((getStatusClass(status) != "wait" && amount != 0 && paidAmount == 0) ? " void" : "") +
	((amount < 0) ? " neg" : "")
);
const getSignedAmount = (amount) => (amount > 0 ? "+" : "") + getCommaNumber(amount);

/**
 * [이용권 종류별 매출 목록]
 */
 
export const DetailList = (props) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const navigationType = useNavigationType();

	const {
		searchPartner,
		searchBranch,
		searchDate,
		searchPeriodType,
		searchFilter,
		dataList,
		pageState
	} = useSelector(state => state.sales.voucherInfo);
	const partnerInfo = useSelector(state => state.partner);
	const [expanded, setExpanded] = useState({});

	useLayoutEffect(() => {
		dispatch(fetchDetailPageScroll("restore"));
	}, []);

	useEffect(() => {
		const isUpdate = (
			!dataList || navigationType != "POP" || (
				searchPartner != dataList.searchInfo.searchPartner ||
				searchBranch != dataList.searchInfo.searchBranch ||
				searchDate != dataList.searchInfo.searchDate ||
				searchPeriodType != dataList.searchInfo.searchPeriodType ||
				searchFilter != dataList.searchInfo.searchFilter
			)
		);
		if(searchDate && searchPeriodType && isUpdate) {
			if(pageState != undefined) {
				dispatch(updateVoucherInfoList(false));
			}
		}
	}, [searchPartner, searchBranch, searchDate, searchPeriodType, searchFilter]);

	const eventHandler = {
		update : function() {
			dispatch(updateVoucherInfoList(true));
		},
		detail : (orderType, sequence, serviceType) => {
			navigate("/sales/" + orderType + "/payment/" + sequence, {
				state : {serviceType : serviceType}
			});
		},
		toggle : (e, key) => {
			e.stopPropagation();
			e.preventDefault();
			setExpanded(prev => ({...prev, [key] : !prev[key]}));
		},
		changeFilter : (name, value) => {
			dispatch(fetchVoucherInfo({
				searchFilter : value
			}));
		},
		changeBranch : (branch) => {
			dispatch(fetchVoucherInfo({
				searchBranch : branch,
			}));
		}
	};

	const render = () => {
		const getPaymentList = () => {
			let li = ((dataList || {}).paymentList || []).map(item => {
				// 카드 상태는 카드의 displayStatus로 정한다(대표 결제의 paymentStatus는 쓰지 않는다).
				const orderState = getStatusClass(item.displayStatus) || ((item.paymentKind == "REFUND") ? "refund" : "");
				const getSummary = () => {
					const datetime = new Date(item.paymentDatetime).format("sm. sd. sw · hh:MM");
					const memberName = item.memberName || "-";
					return (
						<Fragment>
							<span>{datetime}</span>
							<span>{memberName}</span>
						</Fragment>
					);
				};
				const getPaymentType = () => {
					const paymentTypeName = getPaymentTypeName(item.paymentType);
					const paymentKind = (orderState == "refund") ? "환불" : (orderState == "cancel") ? "취소" : (orderState == "wait") ? "결제대기" : "결제";
					return paymentTypeName + " " + paymentKind;
				};
				const sequence = item.seqOrderPayment || item.seqRefundInfo;
				const orderType = (item.seqRefundInfo) ? "refund" : "order";
				const orderName = (item.paymentClassification == "TRANSFER") ? "양도 수수료" : (item.orderName || "").recoverTag();
				const serviceTypeName = getServiceTypeName(item.serviceType);
				const rowKey = orderType + "-" + sequence;
				const passes = item.passes || [];
				const isMulti = passes.length >= 2;
				const isFilter = !!searchFilter;
				// 큰 금액은 항상 결제금액이다. 필터 분류 금액은 줄이 2개 이상일 때만 작은 글씨로 붙인다.
				const showFilterDetail = isFilter && item.filterAmount != null && isMulti;
				const getKind = () => {
					const types = [];
					passes.forEach(p => {
						if(!types.includes(p.serviceType)) {
							types.push(p.serviceType);
						}
					});
					if(!types.length) {
						return (<h5>{serviceTypeName}</h5>);
					}
					if(types.length == 1) {
						return (<h5>{getServiceTypeName(types[0])}</h5>);
					}
					return (
						<div className="kind">
							{passes.some(p => p.packageName) && <span className="chip pkg">패키지 포함</span>}
							<span className="chip">{types.map(t => getServiceTypeName(t)).join(" · ")}</span>
						</div>
					);
				};
				// 복합결제 카드: 결제가 2건 이상이면 "결제 N건 ▾"로 모든 결제를 펼쳐 보인다(숨기는 결제 없음)
				const payments = item.payments || [];
				const hasPayments = payments.length >= 2;
				// 취소선은 카드의 금액이 계산되지 않을 때만(카드 순액 0, 결제는 0이 아닌 금액이 있음). 결제대기 카드는 긋지 않는다
				const cardVoid = item.paymentAmount == 0 && payments.some(p => p.paymentAmount != 0) && item.displayStatus != "WAIT";
				// 카드 키는 최대 seq라 취소 기록일 수 있다. 상세는 마지막 정상(PAID) 결제로 연다
				const paidSeqs = payments.filter(p => p.displayStatus == "PAID" && p.seqOrderPayment).map(p => p.seqOrderPayment);
				const linkSequence = (orderType == "order" && paidSeqs.length) ? Math.max(...paidSeqs) : sequence;
				const payKey = rowKey + ":pay";
				const payOpen = hasPayments && !!expanded[payKey];
				const getPaymentLabel = (p) => {
					const cls = getStatusClass(p.displayStatus || p.paymentStatus);
					return getPaymentTypeName(p.paymentType) + " " + ((cls == "cancel") ? "취소" : (cls == "wait") ? "결제대기" : (p.paymentAmount < 0) ? "환불" : "결제");
				};
				const getPaymentLines = () => {
					if(!payOpen) {
						return null;
					}
					return (
						<div className="passes pays">
							{payments.map((p, i) => (
								<div key={p.seqOrderPayment || i} className={"pass pay" + getMoneyClass(p.displayStatus || p.paymentStatus, p.paymentAmount, p.paidAmount)}>
									<span className="s">{getPaymentLabel(p)}</span>
									<span className="a">{getSignedAmount(p.paymentAmount)}원</span>
								</div>
							))}
						</div>
					);
				};
				// 결제별 원래 줄(details)은 결제 목록을 펼쳤을 때 줄 아래에 보인다
				const getDetails = (p) => {
					// 0원 줄은 숨긴다(비율 배분으로 0원 이용권마다 생기는 줄). 전부 0원이면 줄을 그리지 않는다
					const details = (p.details || []).filter(d => d.amount != 0);
					if(!payOpen || !details.length) {
						return null;
					}
					return details.map((d, i) => {
						const pay = payments.find(x => x.seqOrderPayment == d.seqOrderPayment);
						const cls = pay ? getMoneyClass(pay.displayStatus || pay.paymentStatus, d.amount, pay.paidAmount) : ((d.amount < 0) ? " neg" : "");
						return (
							<div key={"d" + i} className={"pass detail" + cls}>
								<span className="s">{pay ? getPaymentLabel(pay) : "결제"}</span>
								<span className="a">{getSignedAmount(d.amount)}원</span>
							</div>
						);
					});
				};
				// 환불 위약금: 지급액에서 빠진 금액이라 +로 보여준다
				const getPenalty = () => {
					if(!(item.refundPenalty > 0)) {
						return null;
					}
					return (
						<div className="pass penalty">
							<span className="t">위약금</span>
							<span className="s" />
							<span className="a">+{getCommaNumber(item.refundPenalty)}원</span>
						</div>
					);
				};
				const getPasses = () => {
					const getRow = (p, i) => {
						const cls = (!isFilter) ? "" : (p.serviceType == searchFilter) ? " hit" : " dim";
						return (
							<Fragment key={i}>
								<div className={"pass" + cls + ((p.amount < 0) ? " neg" : "")}>
									<i className="dot" style={{background : SERVICE_COLOR[p.serviceType] || SERVICE_COLOR.ETC}} />
									<span className="t">{getServiceTypeName(p.serviceType)}</span>
									<span className="s">{p.serviceName}</span>
									<span className="a">{getCommaNumber(p.amount)}원</span>
								</div>
								{getDetails(p)}
							</Fragment>
						);
					};
					if(!isMulti) {
						const penalty = getPenalty();
						// 줄 1개짜리 환불 카드는 환불금액 줄을 그린다. 결제를 펼쳤을 때는 결제별 줄도 보인다.
						const single = (passes.length == 1 && (orderState == "refund" || payOpen)) ? getRow(passes[0], 0) : null;
						return (single || penalty) ? (<div className="passes">{single}{penalty}</div>) : null;
					}
					// 패키지는 한 덩어리로 유지하고, 필터에 걸리는 덩어리를 위로 올린다
					const blocks = [];
					passes.forEach(p => {
						const last = blocks[blocks.length - 1];
						if(p.packageName && last && last.packageName && last.seqOrderPricing == p.seqOrderPricing) {
							last.rows.push(p);
						} else {
							blocks.push({packageName : p.packageName, seqOrderPricing : p.seqOrderPricing, rows : [p]});
						}
					});
					if(isFilter) {
						const hit = (b) => b.rows.some(p => p.serviceType == searchFilter) ? 1 : 0;
						blocks.sort((a, b) => hit(b) - hit(a));
					}
					const list = [];
					blocks.forEach(b => b.rows.forEach(p => list.push({...p, block : b})));
					const limit = (expanded[rowKey] || payOpen) ? list.length : 3;
					const shown = list.slice(0, limit);
					const groups = [];
					shown.forEach(p => {
						const last = groups[groups.length - 1];
						if(last && last.block === p.block) {
							last.rows.push(p);
						} else {
							groups.push({block : p.block, rows : [p]});
						}
					});
					const more = list.length - limit;
					return (
						<div className="passes">
							{groups.map((g, gi) => g.block.packageName ? (
								<div key={gi} className="grp">
									<div className="grp-title">패키지 · {g.block.packageName}</div>
									{g.rows.map(getRow)}
								</div>
							) : g.rows.map(getRow))}
							{getPenalty()}
							{more > 0 && <div className="toggle" onClick={(e) => {eventHandler.toggle(e, rowKey)}}>이용권 {more}개 더보기 ▾</div>}
							{more <= 0 && list.length > 3 && !payOpen && <div className="toggle" onClick={(e) => {eventHandler.toggle(e, rowKey)}}>접기 ▴</div>}
						</div>
					);
				};
				return (
					<li key={rowKey} className={[orderState, cardVoid ? "void" : "", item.paymentAmount < 0 ? "neg" : ""].join(" ").trim()}>
						<a onClick={() => {eventHandler.detail(orderType, linkSequence, item.serviceType)}}>
							<div className="left">
								{getKind()}
								<h4>{orderName || "-"}</h4>
								<h5>{getSummary()}</h5>
							</div>
							<div className="right">
								<h6>&nbsp;</h6>
								<h4>{getCommaNumber(item.paymentAmount)}원</h4>
								{hasPayments && (
									<h5 className="total toggle" onClick={(e) => {eventHandler.toggle(e, payKey)}}>결제 {payments.length}건 {payOpen ? "▴" : "▾"}</h5>
								)}
								{showFilterDetail && (
									<h5 className="total">{getServiceTypeName(searchFilter)} {getCommaNumber(item.filterAmount)}원</h5>
								)}
								<h5>{getPaymentType()}</h5>
							</div>
							{getPaymentLines()}
							{getPasses()}
						</a>
					</li>
				)
			});
			if(!li.length && pageState != undefined) {
				li = <li className="empty">검색 결과가 없습니다.</li>
			}
			return (<ul className="color">{li}</ul>);
		};

		const filterList = [{
			label : "전체",
			value : "",
		}, {
			label : "개인레슨",
			value : "APPOINTMENT"
		}, {
			label : "그룹수업",
			value : "CLASS"
		}, {
			label : "시설이용",
			value : "PLACE"
		}, {
			label : "옵션이용",
			value : "OPTION"
		}, {
			// CS25-8980: PACKAGE가 서버 Filter enum에서 빠졌다. 보내면 400.
			label : "양도 수수료",
			value : "TRANSFER_COMMISSION"
		}, {
			label : "기타",
			value : "ETC"
		}];

		return (
			<Wrapper>
				<Filter
					title="이용권 종류 선택" value={searchFilter}
					options={filterList} onChange={eventHandler.changeFilter}
					isSticky={true}
				/>
				<div className="list">
					{getPaymentList()}
				</div>
				<Observer isActive={pageState} isLoading={true} onVisible={eventHandler.update} />
				<BottomBranch searchBranch={searchBranch} onChange={eventHandler.changeBranch} />
			</Wrapper>
		);
	};
	return render();
};

const Wrapper = styled.div`
.list								{padding:0 24px}
.list li + li						{border-top:1px solid var(--lightBorder)}
.list li.empty						{padding:20px 0; border:none; text-align:center; opacity:0.66}
.list a								{display:flex; flex-wrap:wrap; align-items:center; justify-content:space-between; padding:25px 0}
.list h4							{margin:5px 0 7px; font-size:1rem; font-weight:500}
.list h5							{font-size:var(--13px); color:var(--gray40)}
.list h6							{font-size:var(--12px); color:#7783ad}
.list h5 span + span:before			{content:"|"; margin:0 0.5em; color:var(--gray); opacity:0.5}

.list .left							{flex:1; min-width:0}
.list .right						{padding-left:10px; text-align:right}
.list .right h4						{white-space:nowrap; font-size:var(--18px); font-weight:600; color:var(--blue)}
.list li.void .right h4 			{text-decoration:line-through; color:var(--gray40)}
.list li.refund .right h4,
.list li.neg .right h4				{color:var(--red)}
.list li.wait .right h4				{color:var(--gray40)}
.list li.refund .right h4:before	{content:""}
.list .kind							{display:flex; flex-wrap:wrap; align-items:center; gap:4px}
.list .chip							{padding:1px 6px; border-radius:4px; background:var(--lightBorder); font-size:var(--12px); color:var(--gray40)}
.list .chip.pkg						{background:#eaf0ff; color:var(--blue)}
.list .right h5.total				{margin-top:2px; font-size:var(--12px)}
.list .passes						{flex-basis:100%; margin-top:10px; padding:6px 12px; border-radius:10px; background:#f6f7f9}
.list .grp							{margin:6px 0; padding-left:8px; border-left:2px solid #c9d6fb}
.list .grp-title					{margin-bottom:2px; font-size:var(--12px); color:var(--blue)}
.list .pass							{display:flex; align-items:center; gap:8px; padding:5px 0; font-size:var(--13px)}
.list .pass .dot					{flex:none; width:7px; height:7px; border-radius:50%}
.list .pass .t						{flex:none; width:52px; font-size:var(--12px); color:var(--gray40)}
.list .pass .s						{flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap}
.list .pass .a						{white-space:nowrap; font-weight:500}
.list .pass.void .a					{text-decoration:line-through; color:var(--gray40)}
.list .pass.neg .a					{color:var(--red)}
.list .pass.dim						{opacity:.38}
.list .pass.hit .s,
.list .pass.hit .a					{font-weight:700}
.list .pass.pay,
.list .pass.detail					{justify-content:space-between}
.list .pass.detail					{padding:2px 0 2px 15px; font-size:var(--12px); color:var(--gray40)}
.list .pass.wait .a					{color:var(--gray40)}
.list .toggle						{padding:4px 0 2px; font-size:var(--12px); color:var(--gray40); cursor:pointer; user-select:none}
`;

export default DetailList;